import type { BlockFieldBody, Condition, FieldBody, Rules } from '@/shared/api/generated/model';

/**
 * Поле формы — то, что нужно редактору, откуда бы поле ни пришло: из схемы
 * типа, из блока или из повторяющейся группы. Три описания в спеке разные по
 * происхождению, а редактор один.
 */
export interface FormField {
  /** Ключ для разметки: у полей блока своего идентификатора нет. */
  id: string;
  key: string;
  name: string;
  type: string;
  required: boolean;
  hint: string;
  options: Record<string, unknown>;
  widget: string;
  rules: Rules;
  showIf: Condition | undefined;
  width: 'full' | 'half';
  tab: string;
  /** Поля повторяющейся группы. */
  subfields: FormField[];
  /** У поля есть переводы на другие языки сайта. */
  translatable: boolean;
}

/** Поля типа деревом: верхний уровень, у групп — их поля. Скрытых нет. */
export function fromFieldBodies(list: readonly FieldBody[]): FormField[] {
  const visible = list.filter((item) => !item.hidden);

  return visible
    .filter((item) => item.parent_id === undefined)
    .map((item) => ({
      ...fromFieldBody(item),
      subfields: visible.filter((child) => child.parent_id === item.id).map(fromFieldBody),
    }));
}

function fromFieldBody(item: FieldBody): FormField {
  return {
    id: item.id,
    key: item.key,
    name: item.name,
    type: item.type,
    required: item.required,
    hint: item.hint,
    options: item.options,
    widget: item.widget,
    rules: item.rules,
    showIf: item.show_if,
    width: item.width,
    tab: item.tab,
    subfields: [],
    translatable: item.translatable,
  };
}

/** Поля блока — встроенного или своего. */
export function fromBlockFields(list: readonly BlockFieldBody[], prefix: string): FormField[] {
  return list.map((item) => ({
    id: `${prefix}:${item.key}`,
    key: item.key,
    name: item.name,
    type: item.type,
    required: item.required,
    hint: item.hint,
    options: item.options,
    widget: item.widget,
    rules: item.rules,
    showIf: item.show_if,
    width: item.width,
    tab: '',
    subfields: fromBlockFields(item.fields ?? [], `${prefix}:${item.key}`),
    translatable: false,
  }));
}

/**
 * Видно ли поле при этих значениях — то же правило, что у сервера
 * (internal/content: visible). Скрытое условием поле не обязательно, и его
 * значение не стирается: условие может снова стать истинным.
 */
export function isVisible(field: FormField, values: Record<string, unknown>): boolean {
  const condition = field.showIf;

  if (condition === undefined) {
    return true;
  }

  const value = values[condition.field];

  switch (condition.op) {
    case 'filled':
      return !isEmpty(value);
    case 'empty':
      return isEmpty(value);
    case 'equals':
      return sameValue(value, condition.value);
    case 'not_equals':
      return !sameValue(value, condition.value);
    default:
      return true;
  }
}

function isEmpty(value: unknown): boolean {
  if (value === null || value === undefined) {
    return true;
  }

  if (typeof value === 'string') {
    return value.trim() === '';
  }

  return Array.isArray(value) && value.length === 0;
}

function sameValue(a: unknown, b: unknown): boolean {
  if (typeof a === 'number' && typeof b === 'number') {
    return a === b;
  }

  return String(a) === String(b);
}
